import { useState, useEffect, useRef } from 'react'
import { servicesAPI, buildAPI } from '../../services/api'

import { logger } from '@/utils/logger';
interface ServiceStatus {
  name: string
  display_name: string
  status: string
  is_running: boolean
  is_enabled: boolean
  uptime: string | null
  memory_usage: string | null
  pid: number | null
  last_restart: string | null
}

interface BuildRunSummary {
  id: number
  app: string
  status: string
  exit_code: number | null
  output_truncated: boolean
  started_at: string
  finished_at: string | null
}

interface BuildRunDetail extends BuildRunSummary {
  output: string
  cancel_requested: boolean
}

interface BuildAppStatus {
  app: string
  display_name: string
  last_run: BuildRunSummary | null
}

const BUILD_TERMINAL_STATUSES = ['success', 'failed', 'timeout', 'cancelled', 'unknown']

export default function ServicesManagement() {
  const [services, setServices] = useState<ServiceStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedService, setSelectedService] = useState<string | null>(null)
  const [logs, setLogs] = useState<string>('')
  const [showLogs, setShowLogs] = useState(false)
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [restartingService, setRestartingService] = useState<string | null>(null)
  const [showRestartConfirm, setShowRestartConfirm] = useState<string | null>(null)

  // Admin-Konsole: Frontend-Builds (Ausbaustufe A)
  const [consoleEnabled, setConsoleEnabled] = useState(false)
  const [consoleReason, setConsoleReason] = useState<string | null>(null)
  const [builds, setBuilds] = useState<BuildAppStatus[]>([])
  const [startingBuildApp, setStartingBuildApp] = useState<string | null>(null)
  const [buildConfirmApp, setBuildConfirmApp] = useState<string | null>(null)
  const [buildConfirmInput, setBuildConfirmInput] = useState('')
  const [rollbackConfirmApp, setRollbackConfirmApp] = useState<string | null>(null)
  const [viewingRun, setViewingRun] = useState<BuildRunDetail | null>(null)
  const [autoScroll, setAutoScroll] = useState(true)
  const pollIntervalRef = useRef<number | null>(null)
  const outputRef = useRef<HTMLPreElement | null>(null)

  useEffect(() => {
    loadServices()
    loadBuilds()
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadServices(true)
      loadBuilds()
    }, 30000)
    return () => {
      clearInterval(interval)
      stopPolling()
    }
  }, [])

  useEffect(() => {
    if (autoScroll && outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight
    }
  }, [viewingRun?.output])

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      window.clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  const pollRun = async (runId: number) => {
    try {
      const response = await buildAPI.getRun(runId)
      setViewingRun(response.data)
      if (BUILD_TERMINAL_STATUSES.includes(response.data.status)) {
        stopPolling()
        loadBuilds()
      }
    } catch (error) {
      logger.error('Error polling build run:', error)
    }
  }

  const startPolling = (runId: number) => {
    stopPolling()
    pollIntervalRef.current = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return
      pollRun(runId)
    }, 1000)
  }

  const loadBuilds = async () => {
    try {
      const response = await buildAPI.getApps()
      setConsoleEnabled(response.data.console_enabled)
      setConsoleReason(response.data.console_reason)
      setBuilds(response.data.apps)
    } catch (error) {
      logger.error('Error loading builds:', error)
    }
  }

  const openRunModal = (runId: number, app: string) => {
    setAutoScroll(true)
    setViewingRun({
      id: runId,
      app,
      status: 'running',
      exit_code: null,
      output_truncated: false,
      started_at: new Date().toISOString(),
      finished_at: null,
      output: '',
      cancel_requested: false,
    })
    startPolling(runId)
    pollRun(runId)
  }

  const handleCloseRunModal = () => {
    setViewingRun(null)
    stopPolling()
  }

  const handleOutputScroll = () => {
    const el = outputRef.current
    if (!el) return
    setAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 40)
  }

  const handleConfirmBuildStart = async () => {
    if (!buildConfirmApp) return
    const app = buildConfirmApp
    setBuildConfirmApp(null)
    setBuildConfirmInput('')
    setStartingBuildApp(app)
    try {
      const response = await buildAPI.start(app)
      openRunModal(response.data.run_id, app)
    } catch (error: any) {
      logger.error('Error starting build:', error)
      alert(error?.response?.data?.detail || 'Fehler beim Starten des Builds')
    } finally {
      setStartingBuildApp(null)
      loadBuilds()
    }
  }

  const handleCancelRun = async () => {
    if (!viewingRun) return
    try {
      await buildAPI.cancel(viewingRun.id)
    } catch (error) {
      logger.error('Error cancelling build:', error)
    }
  }

  const handleRollback = async (app: string) => {
    setRollbackConfirmApp(null)
    try {
      const response = await buildAPI.rollback(app)
      if (!response.data.success) {
        alert(response.data.message)
      }
    } catch (error) {
      logger.error('Error rolling back build:', error)
      alert('Fehler beim Rollback')
    } finally {
      loadBuilds()
    }
  }

  const getBuildStatusTag = (status?: string | null) => {
    switch (status) {
      case 'running':
        return { className: 'wc-tag-accent', label: 'Läuft' }
      case 'success':
        return { className: 'wc-tag-accent', label: 'Erfolgreich' }
      case 'failed':
        return { className: 'wc-tag-outline', style: { borderColor: '#fca5a5', color: '#fca5a5' }, label: 'Fehlgeschlagen' }
      case 'timeout':
        return { className: 'wc-tag-outline', style: { borderColor: '#fca5a5', color: '#fca5a5' }, label: 'Zeitüberschreitung' }
      case 'cancelled':
        return { className: 'wc-tag-neutral', label: 'Abgebrochen' }
      case 'unknown':
        return { className: 'wc-tag-neutral', label: 'Unbekannt' }
      default:
        return { className: 'wc-tag-neutral', label: 'Nie gebaut' }
    }
  }

  const loadServices = async (silent = false) => {
    if (!silent) setLoading(true)
    else setRefreshing(true)

    try {
      const response = await servicesAPI.getAll()
      setServices(response.data.services)
    } catch (error) {
      logger.error('Error loading services:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleRestartService = async (serviceName: string) => {
    setRestartingService(serviceName)
    setShowRestartConfirm(null)

    try {
      await servicesAPI.restart(serviceName)
      // Refresh service list after restart
      setTimeout(() => loadServices(), 2000)
    } catch (error) {
      logger.error('Error restarting service:', error)
      alert('Fehler beim Neustarten des Services')
    } finally {
      setRestartingService(null)
    }
  }

  const handleShowLogs = async (serviceName: string) => {
    setSelectedService(serviceName)
    setShowLogs(true)
    setLoadingLogs(true)

    try {
      const response = await servicesAPI.getLogs(serviceName, 200)
      setLogs(response.data.logs)
    } catch (error) {
      logger.error('Error loading logs:', error)
      setLogs('Fehler beim Laden der Logs')
    } finally {
      setLoadingLogs(false)
    }
  }

  const getStatusTag = (status: string, isRunning: boolean) => {
    if (isRunning) return { className: 'wc-tag-accent', label: 'Läuft' }
    if (status === 'failed') return { className: 'wc-tag-outline', style: { borderColor: '#fca5a5', color: '#fca5a5' }, label: 'Fehlgeschlagen' }
    return { className: 'wc-tag-neutral', label: 'Gestoppt' }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Service-Verwaltung</h1>
          <p className="wc-muted" style={{ fontSize: 14, margin: '4px 0 0' }}>Backend-Services verwalten und überwachen</p>
        </div>
        <button onClick={() => loadServices()} disabled={refreshing} className="wc-btn wc-btn-secondary">
          {refreshing ? 'Aktualisiere...' : 'Aktualisieren'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
        {services.map((service) => {
          const tag = getStatusTag(service.status, service.is_running)
          return (
            <div key={service.name} className="wc-card wc-elev-sm" style={{ gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div>
                  <h3 className="wc-card-title" style={{ margin: 0 }}>{service.display_name}</h3>
                  <p className="wc-muted wc-mono" style={{ fontSize: 12, margin: '2px 0 0' }}>{service.name}</p>
                </div>
                <span className={`wc-tag ${tag.className}`} style={tag.style}>{tag.label}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="wc-muted">Status:</span>
                  <span>{service.status}</span>
                </div>
                {service.pid && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="wc-muted">PID:</span>
                    <span className="wc-mono">{service.pid}</span>
                  </div>
                )}
                {service.memory_usage && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="wc-muted">Speicher:</span>
                    <span>{service.memory_usage}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="wc-muted">Autostart:</span>
                  <span>{service.is_enabled ? 'Aktiviert' : 'Deaktiviert'}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid var(--wc-divider)' }}>
                <button
                  onClick={() => setShowRestartConfirm(service.name)}
                  disabled={restartingService === service.name}
                  className="wc-btn wc-btn-primary"
                  style={{ flex: 1 }}
                >
                  {restartingService === service.name ? 'Wird neugestartet...' : 'Neustart'}
                </button>
                <button onClick={() => handleShowLogs(service.name)} className="wc-btn wc-btn-secondary" style={{ flex: 1 }}>
                  Logs anzeigen
                </button>
              </div>
            </div>
          )
        })}
      </div>

      <div style={{ marginTop: 32 }}>
        <h2 style={{ fontSize: 20, margin: '0 0 4px' }}>Frontend-Builds</h2>
        <p className="wc-muted" style={{ fontSize: 14, margin: '0 0 16px' }}>
          Build der Frontend-Apps ohne SSH-Zugang starten (Ausbaustufe A der Admin-Konsole)
        </p>
        {!consoleEnabled && (
          <div className="wc-alert wc-alert-info" style={{ marginBottom: 16 }}>
            Admin-Konsole ist deaktiviert{consoleReason ? `: ${consoleReason}` : ''}.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {builds.map((b) => {
            const tag = getBuildStatusTag(b.last_run?.status)
            const isRunning = b.last_run?.status === 'running'
            return (
              <div key={b.app} className="wc-card wc-elev-sm" style={{ gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div>
                    <h3 className="wc-card-title" style={{ margin: 0 }}>{b.display_name}</h3>
                    <p className="wc-muted wc-mono" style={{ fontSize: 12, margin: '2px 0 0' }}>{b.app}</p>
                  </div>
                  <span className={`wc-tag ${tag.className}`} style={tag.style}>{tag.label}</span>
                </div>

                {b.last_run && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span className="wc-muted">Letzter Lauf:</span>
                      <span>{new Date(b.last_run.started_at).toLocaleString('de-DE')}</span>
                    </div>
                    {b.last_run.exit_code !== null && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span className="wc-muted">Exit-Code:</span>
                        <span className="wc-mono">{b.last_run.exit_code}</span>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid var(--wc-divider)' }}>
                  <button
                    onClick={() => { setBuildConfirmApp(b.app); setBuildConfirmInput('') }}
                    disabled={!consoleEnabled || startingBuildApp === b.app || isRunning}
                    className="wc-btn wc-btn-primary"
                    style={{ flex: 1 }}
                  >
                    {isRunning ? 'Build läuft...' : startingBuildApp === b.app ? 'Wird gestartet...' : 'Build starten'}
                  </button>
                  {b.last_run && (
                    <button onClick={() => openRunModal(b.last_run!.id, b.app)} className="wc-btn wc-btn-secondary" style={{ flex: 1 }}>
                      Ausgabe
                    </button>
                  )}
                </div>
                {b.last_run?.status === 'success' && (
                  <button
                    onClick={() => setRollbackConfirmApp(b.app)}
                    className="wc-btn wc-btn-ghost"
                    style={{ fontSize: 12, alignSelf: 'flex-start' }}
                  >
                    Letzten Build zurücknehmen
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Restart Confirmation Dialog */}
      {showRestartConfirm && (
        <div className="wc-modal-overlay" onClick={() => setShowRestartConfirm(null)}>
          <div className="wc-modal wc-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>Service neu starten?</h3>
            <p className="wc-card-body" style={{ margin: 0 }}>
              Möchten Sie den Service <strong>{services.find(s => s.name === showRestartConfirm)?.display_name}</strong> wirklich neu starten?
              <br /><br />
              Der Service wird kurzzeitig nicht verfügbar sein.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setShowRestartConfirm(null)} className="wc-btn wc-btn-secondary" style={{ flex: 1 }}>
                Abbrechen
              </button>
              <button onClick={() => handleRestartService(showRestartConfirm)} className="wc-btn wc-btn-primary" style={{ flex: 1 }}>
                Ja, neu starten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogs && (
        <div className="wc-modal-overlay" onClick={() => setShowLogs(false)}>
          <div className="wc-modal wc-modal-lg" style={{ maxHeight: '80vh' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <h3 className="wc-card-title" style={{ margin: 0 }}>Service Logs</h3>
                <p className="wc-muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
                  {services.find(s => s.name === selectedService)?.display_name}
                </p>
              </div>
              <button onClick={() => setShowLogs(false)} className="wc-btn wc-btn-icon wc-btn-ghost" aria-label="Schließen">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {loadingLogs ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                <div className="wc-spinner" />
              </div>
            ) : (
              <pre
                className="wc-mono"
                style={{
                  background: 'var(--wc-bg)',
                  border: '1px solid var(--wc-divider)',
                  color: '#a7f3d0',
                  padding: 16,
                  borderRadius: 'var(--wc-radius-md)',
                  fontSize: 12,
                  overflowX: 'auto',
                  overflowY: 'auto',
                  maxHeight: '50vh',
                  margin: 0,
                }}
              >
                {logs || 'Keine Logs verfügbar'}
              </pre>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => selectedService && handleShowLogs(selectedService)}
                disabled={loadingLogs}
                className="wc-btn wc-btn-secondary"
              >
                Aktualisieren
              </button>
              <button onClick={() => setShowLogs(false)} className="wc-btn wc-btn-primary" style={{ flex: 1 }}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Build Confirmation Dialog */}
      {buildConfirmApp && (
        <div className="wc-modal-overlay" onClick={() => setBuildConfirmApp(null)}>
          <div className="wc-modal wc-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>Build starten?</h3>
            <p className="wc-card-body" style={{ margin: 0 }}>
              Ein Build ersetzt nach erfolgreichem Abschluss die aktuell ausgelieferte Frontend-Version von{' '}
              <strong>{builds.find(b => b.app === buildConfirmApp)?.display_name}</strong>.
              Zum Bestätigen App-Namen eingeben: <code className="wc-mono">{buildConfirmApp}</code>
            </p>
            <input
              type="text"
              value={buildConfirmInput}
              onChange={(e) => setBuildConfirmInput(e.target.value)}
              placeholder={buildConfirmApp}
              className="wc-input"
              autoFocus
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setBuildConfirmApp(null)} className="wc-btn wc-btn-secondary" style={{ flex: 1 }}>
                Abbrechen
              </button>
              <button
                onClick={handleConfirmBuildStart}
                disabled={buildConfirmInput !== buildConfirmApp}
                className="wc-btn wc-btn-primary"
                style={{ flex: 1 }}
              >
                Build starten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rollback Confirmation Dialog */}
      {rollbackConfirmApp && (
        <div className="wc-modal-overlay" onClick={() => setRollbackConfirmApp(null)}>
          <div className="wc-modal wc-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>Build zurücknehmen?</h3>
            <p className="wc-card-body" style={{ margin: 0 }}>
              Der aktuelle Build von <strong>{builds.find(b => b.app === rollbackConfirmApp)?.display_name}</strong>{' '}
              wird durch den vorherigen Stand (<span className="wc-mono">dist.prev</span>) ersetzt.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setRollbackConfirmApp(null)} className="wc-btn wc-btn-secondary" style={{ flex: 1 }}>
                Abbrechen
              </button>
              <button onClick={() => handleRollback(rollbackConfirmApp)} className="wc-btn wc-btn-primary" style={{ flex: 1 }}>
                Ja, zurücknehmen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Build Output Modal (Polling) */}
      {viewingRun && (
        <div className="wc-modal-overlay" onClick={handleCloseRunModal}>
          <div className="wc-modal wc-modal-lg" style={{ maxHeight: '80vh' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <h3 className="wc-card-title" style={{ margin: 0 }}>Build-Ausgabe</h3>
                <p className="wc-muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
                  {builds.find(b => b.app === viewingRun.app)?.display_name || viewingRun.app} · Run #{viewingRun.id}
                </p>
              </div>
              <button onClick={handleCloseRunModal} className="wc-btn wc-btn-icon wc-btn-ghost" aria-label="Schließen">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <pre
              ref={outputRef}
              onScroll={handleOutputScroll}
              className="wc-mono"
              style={{
                background: 'var(--wc-bg)',
                border: '1px solid var(--wc-divider)',
                color: '#a7f3d0',
                padding: 16,
                borderRadius: 'var(--wc-radius-md)',
                fontSize: 12,
                overflowX: 'auto',
                overflowY: 'auto',
                maxHeight: '50vh',
                margin: 0,
              }}
            >
              {viewingRun.output || 'Noch keine Ausgabe...'}
            </pre>

            {viewingRun.output_truncated && (
              <p className="wc-muted" style={{ fontSize: 12, margin: 0 }}>⚠ Ausgabe wurde gekappt (Limit erreicht)</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span className="wc-muted">Status: {getBuildStatusTag(viewingRun.status).label}</span>
              {viewingRun.exit_code !== null && <span className="wc-muted">Exit-Code: {viewingRun.exit_code}</span>}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              {viewingRun.status === 'running' && (
                <button onClick={handleCancelRun} className="wc-btn wc-btn-secondary" style={{ flex: 1 }}>
                  Abbrechen
                </button>
              )}
              <button onClick={handleCloseRunModal} className="wc-btn wc-btn-primary" style={{ flex: 1 }}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="wc-alert wc-alert-info" style={{ marginTop: 24 }}>
        <strong>Hinweise zur Service-Verwaltung:</strong>
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          <li>Die Status-Anzeige aktualisiert sich automatisch alle 30 Sekunden</li>
          <li>Ein Neustart dauert in der Regel 2-5 Sekunden</li>
          <li>Während des Neustarts ist der Service kurzzeitig nicht erreichbar</li>
          <li>Die Logs zeigen die letzten 200 Zeilen der Systemlogs</li>
        </ul>
      </div>
    </div>
  )
}
